import { useCallback, useEffect, useMemo, useState } from "react";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  useQpmOfferChangeDocumentProfileMutation,
  useQpmOfferReloadOfferConfigSnapshotMutation,
} from "@/services/backend/qpm/offers/api/offer-api";
import {
  ChangeOfferDocumentProfileRequest,
  Offer,
} from "@/services/backend/qpm/offers/types/offer";
import {
  DocumentProfileFieldModes,
  DocumentProfilePatchField,
  OfferDocumentProfileDraft,
  OfferDocumentProfileForm,
  validateOfferDocumentProfileDraft,
} from "@/routes/processes/[processId]/qpm/offers/_components/offer-document-profile-form";
import {
  rtkErrIsValidationError,
  ValidationError,
} from "@/shared/lib/errors/validation-error";
import { useOfferContext } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/offer-context";
import {
  ChangeMode,
  ConfigOrigin,
  ExtensionAreaScope,
  IoTTableMode,
  MainOfferKind,
  OfferType,
  PriorIoTPresenceState,
  SectionMode,
} from "@/services/backend/qpm/offers/types/offer-enum";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";

const toMode = (origin: ConfigOrigin): ChangeMode =>
  origin === ConfigOrigin.Manual ? ChangeMode.Manual : ChangeMode.Automatic;

function toConfigPatch<T extends string | number>(value: T, mode?: ChangeMode) {
  if (mode === ChangeMode.Automatic) {
    return { mode: ChangeMode.Automatic };
  }

  return {
    mode: ChangeMode.Manual,
    value,
  };
}

function buildPatchFromDraft(
  draft: OfferDocumentProfileDraft,
  fieldModes: DocumentProfileFieldModes,
): ChangeOfferDocumentProfileRequest["documentProfile"] {
  const patch: ChangeOfferDocumentProfileRequest["documentProfile"] = {
    offerType: toConfigPatch(draft.offerType, fieldModes.offerType),
  };

  if (draft.offerType === OfferType.Main) {
    patch.mainOfferKind = toConfigPatch(
      draft.mainOfferKind,
      fieldModes.mainOfferKind,
    );
    return patch;
  }

  patch.priorIoTPresenceState = toConfigPatch(
    draft.priorIoTPresenceState,
    fieldModes.priorIoTPresenceState,
  );
  patch.tableMode = toConfigPatch(draft.tableMode, fieldModes.tableMode);

  if (draft.tableMode === SectionMode.Include) {
    patch.ioTInTableMode = toConfigPatch(
      draft.ioTInTableMode,
      fieldModes.ioTInTableMode,
    );
  }

  patch.ioTRentalContractMode = toConfigPatch(
    draft.ioTRentalContractMode,
    fieldModes.ioTRentalContractMode,
  );
  patch.contractAgainstText = toConfigPatch(
    draft.contractAgainstText,
    fieldModes.contractAgainstText,
  );
  patch.targetAreaScope = toConfigPatch(
    draft.targetAreaScope,
    fieldModes.targetAreaScope,
  );
  patch.referencedContractDate = draft.referencedContractDate;

  return patch;
}

export function OfferDocumentProfileEditor({ offer }: { offer: Offer }) {
  const { setValidationError } = useOfferContext();
  const { documentProfile } = offer;
  const extensionProfile = documentProfile.extension;

  const [
    changeDocumentProfile,
    {
      error: changeDocumentProfileError,
      isLoading: isSavingDocumentProfile,
      isSuccess: isChangeDocumentProfileSuccess,
      reset: resetChangeDocumentProfile,
    },
  ] = useQpmOfferChangeDocumentProfileMutation();

  const [reloadSnapshot] = useQpmOfferReloadOfferConfigSnapshotMutation();

  const [offerType, setOfferType] = useState<OfferType>(
    documentProfile.offerType.value,
  );
  const [mainOfferKind, setMainOfferKind] = useState<MainOfferKind>(
    documentProfile.mainOfferKind?.value ?? MainOfferKind.Unspecified,
  );
  const [priorIoTPresenceState, setPriorIoTPresenceState] =
    useState<PriorIoTPresenceState>(
      extensionProfile?.priorIoTPresenceState.value ??
        PriorIoTPresenceState.Unknown,
    );
  const [tableMode, setTableMode] = useState<SectionMode>(
    extensionProfile?.tableMode.value ?? SectionMode.Include,
  );
  const [ioTInTableMode, setIoTInTableMode] = useState<IoTTableMode>(
    extensionProfile?.ioTInTableMode.value ?? IoTTableMode.Exclude,
  );
  const [ioTRentalContractMode, setIoTRentalContractMode] =
    useState<SectionMode>(
      extensionProfile?.ioTRentalContractMode.value ?? SectionMode.Omit,
    );
  const [referencedContractDate, setReferencedContractDate] = useState<
    string | null
  >(extensionProfile?.referencedContractDate ?? null);
  const [contractAgainstText, setContractAgainstText] = useState<string>(
    extensionProfile?.contractAgainstText?.value ?? "",
  );
  const [targetAreaScope, setTargetAreaScope] = useState<ExtensionAreaScope>(
    extensionProfile?.targetAreaScope?.value ?? ExtensionAreaScope.Indoor,
  );
  const [templateSetVersion, setTemplateSetVersion] = useState<
    string | undefined
  >(documentProfile.templateSetVersion);
  const [fieldModes, setFieldModes] = useState<DocumentProfileFieldModes>({
    offerType: toMode(documentProfile.offerType.origin),
    ...(documentProfile.mainOfferKind
      ? { mainOfferKind: toMode(documentProfile.mainOfferKind.origin) }
      : {}),
    ...(extensionProfile
      ? {
          priorIoTPresenceState: toMode(
            extensionProfile.priorIoTPresenceState.origin,
          ),
          tableMode: toMode(extensionProfile.tableMode.origin),
          ioTInTableMode: toMode(extensionProfile.ioTInTableMode.origin),
          ioTRentalContractMode: toMode(
            extensionProfile.ioTRentalContractMode.origin,
          ),
          contractAgainstText: toMode(
            extensionProfile.contractAgainstText?.origin ??
              ConfigOrigin.Derived,
          ),
          targetAreaScope: toMode(
            extensionProfile.targetAreaScope?.origin ?? ConfigOrigin.Derived,
          ),
        }
      : {}),
  });
  const [localValidationMessage, setLocalValidationMessage] = useState<
    string | null
  >(
    validateOfferDocumentProfileDraft({
      offerType,
      mainOfferKind,
      priorIoTPresenceState,
      tableMode,
      ioTInTableMode,
      ioTRentalContractMode,
      referencedContractDate,
      contractAgainstText,
      targetAreaScope,
      templateSetVersion,
    }),
  );

  const documentProfileDraft = useMemo<OfferDocumentProfileDraft>(
    () => ({
      offerType,
      mainOfferKind,
      priorIoTPresenceState,
      tableMode,
      ioTInTableMode,
      ioTRentalContractMode,
      referencedContractDate,
      contractAgainstText,
      targetAreaScope,
      templateSetVersion,
    }),
    [
      offerType,
      mainOfferKind,
      priorIoTPresenceState,
      tableMode,
      ioTInTableMode,
      ioTRentalContractMode,
      referencedContractDate,
      contractAgainstText,
      targetAreaScope,
      templateSetVersion,
    ],
  );

  const [documentProfileRequest, setDocumentProfileRequest] =
    useState<ChangeOfferDocumentProfileRequest>(() => ({
      offerId: offer.id,
      documentProfile: buildPatchFromDraft(documentProfileDraft, fieldModes),
    }));

  const updateRequest = useCallback(
    (
      nextDraft: OfferDocumentProfileDraft,
      nextFieldModes: DocumentProfileFieldModes,
    ) => {
      const validationMessage = validateOfferDocumentProfileDraft(nextDraft);
      setLocalValidationMessage(validationMessage);
      if (validationMessage) {
        return;
      }

      setDocumentProfileRequest((current) => {
        const nextRequest = {
          offerId: offer.id,
          documentProfile: buildPatchFromDraft(nextDraft, nextFieldModes),
        };

        if (JSON.stringify(current) === JSON.stringify(nextRequest)) {
          return current;
        }

        return nextRequest;
      });
    },
    [offer.id],
  );

  const onDraftChange = useCallback(
    (nextDraft: OfferDocumentProfileDraft) => {
      const nextFieldModes = { ...fieldModes };

      if (nextDraft.offerType !== offerType) {
        nextFieldModes.offerType = ChangeMode.Manual;
      }
      if (nextDraft.mainOfferKind !== mainOfferKind) {
        nextFieldModes.mainOfferKind = ChangeMode.Manual;
      }
      if (nextDraft.priorIoTPresenceState !== priorIoTPresenceState) {
        nextFieldModes.priorIoTPresenceState = ChangeMode.Manual;
      }
      if (nextDraft.tableMode !== tableMode) {
        nextFieldModes.tableMode = ChangeMode.Manual;
      }
      if (nextDraft.ioTInTableMode !== ioTInTableMode) {
        nextFieldModes.ioTInTableMode = ChangeMode.Manual;
      }
      if (nextDraft.ioTRentalContractMode !== ioTRentalContractMode) {
        nextFieldModes.ioTRentalContractMode = ChangeMode.Manual;
      }
      if (nextDraft.contractAgainstText !== contractAgainstText) {
        nextFieldModes.contractAgainstText = ChangeMode.Manual;
      }
      if (nextDraft.targetAreaScope !== targetAreaScope) {
        nextFieldModes.targetAreaScope = ChangeMode.Manual;
      }
      setOfferType(nextDraft.offerType);
      setMainOfferKind(nextDraft.mainOfferKind);
      setPriorIoTPresenceState(nextDraft.priorIoTPresenceState);
      setTableMode(nextDraft.tableMode);
      setIoTInTableMode(nextDraft.ioTInTableMode);
      setIoTRentalContractMode(nextDraft.ioTRentalContractMode);
      setReferencedContractDate(nextDraft.referencedContractDate);
      setContractAgainstText(nextDraft.contractAgainstText);
      setTargetAreaScope(nextDraft.targetAreaScope);
      setTemplateSetVersion(nextDraft.templateSetVersion);

      if (JSON.stringify(nextFieldModes) !== JSON.stringify(fieldModes)) {
        setFieldModes(nextFieldModes);
      }

      updateRequest(nextDraft, nextFieldModes);
    },
    [
      contractAgainstText,
      fieldModes,
      ioTInTableMode,
      ioTRentalContractMode,
      mainOfferKind,
      offerType,
      priorIoTPresenceState,
      tableMode,
      targetAreaScope,
      updateRequest,
    ],
  );

  const onFieldModeChange = useCallback(
    (field: DocumentProfilePatchField, mode: ChangeMode) => {
      setFieldModes((currentModes) => {
        if (currentModes[field] === mode) {
          return currentModes;
        }

        const nextFieldModes = { ...currentModes, [field]: mode };
        updateRequest(documentProfileDraft, nextFieldModes);
        return nextFieldModes;
      });
    },
    [documentProfileDraft, updateRequest],
  );

  useDebouncedMutationWithPersistenceStateContextUpdate(
    documentProfileRequest,
    changeDocumentProfile,
    isSavingDocumentProfile,
    changeDocumentProfileError,
    isChangeDocumentProfileSuccess,
    resetChangeDocumentProfile,
    500,
    {
      skip: !!localValidationMessage || offer.immutable,
      toastError: true,
    },
  );

  useEffect(() => {
    if (
      !changeDocumentProfileError ||
      !rtkErrIsValidationError(changeDocumentProfileError)
    ) {
      return;
    }

    setValidationError(changeDocumentProfileError.data as ValidationError);
  }, [changeDocumentProfileError, setValidationError]);

  useEffect(() => {
    if (!isChangeDocumentProfileSuccess) {
      return;
    }
    reloadSnapshot({ offerId: offer.id });

    setValidationError(null);
  }, [
    isChangeDocumentProfileSuccess,
    offer.id,
    reloadSnapshot,
    setValidationError,
  ]);

  return (
    <div className="space-y-3">
      <OfferDocumentProfileForm
        mode="patch"
        value={documentProfileDraft}
        onChange={onDraftChange}
        disabled={offer.immutable}
        fieldModes={fieldModes}
        onFieldModeChange={onFieldModeChange}
        showAllFields
        showTargetOrganismSelection={false}
        showPestDisplayFields={false}
        validationMessage={localValidationMessage}
      />
      <RTKQueryErrorAlert error={changeDocumentProfileError} />
    </div>
  );
}
